import { el, clear } from '../../../core/dom.js';
import type { RescueModule, RescueResult } from '../../rescue-types.js';
import { pickShape, type GhostShape } from './shapes.js';
import './ghost-images.css';

const HISTORY_KEY = 'rescue.ghost-images.history';
const HISTORY_CAP = 200;
const STARE_SEC = 30;
const SVG_NS = 'http://www.w3.org/2000/svg';

interface Attempt { at: string; ms: number }

function readHistory(): Attempt[] {
  try {
    const raw = localStorage.getItem(HISTORY_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    const out: Attempt[] = [];
    for (const v of parsed) {
      if (v && typeof v === 'object'
          && typeof (v as { ms?: unknown }).ms === 'number'
          && typeof (v as { at?: unknown }).at === 'string') {
        out.push({ at: (v as Attempt).at, ms: (v as Attempt).ms });
      }
    }
    return out;
  } catch { return []; }
}

function writeHistory(list: Attempt[]): void {
  try {
    const trimmed = list.slice(-HISTORY_CAP);
    localStorage.setItem(HISTORY_KEY, JSON.stringify(trimmed));
  } catch { /* quota / private mode — ignore */ }
}

function formatMs(ms: number): string {
  const total = Math.round(ms / 100) / 10;
  if (total < 60) return `${total.toFixed(1)}s`;
  const m = Math.floor(total / 60);
  const s = Math.floor(total - m * 60);
  return `${m}m ${s}s`;
}

function svg(tag: string, attrs: Record<string, string>): SVGElement {
  const node = document.createElementNS(SVG_NS, tag) as SVGElement;
  for (const [k, v] of Object.entries(attrs)) node.setAttribute(k, v);
  return node;
}

function buildShapeSvg(shape: GhostShape): SVGElement {
  const root = svg('svg', {
    class: 'ghost-shape',
    viewBox: '0 0 300 300',
    'aria-label': shape.name,
  });
  root.innerHTML = shape.inner;
  return root;
}

function buildGraph(history: readonly Attempt[]): HTMLElement {
  const card = el('div', { class: 'ghost-graph' });
  const head = el('div', { class: 'ghost-graph-head' });
  const best = history.reduce((m, a) => Math.max(m, a.ms), 0);
  head.appendChild(el('span', {}, 'Hold time'));
  head.appendChild(el('span', { class: 'ghost-graph-best' },
    history.length ? `best ${formatMs(best)}` : ''));
  card.appendChild(head);
  if (history.length === 0) {
    card.appendChild(el('div', { class: 'ghost-graph-empty' }, 'No sessions yet — try one round.'));
    return card;
  }
  const recent = history.slice(-30);
  const w = 100;
  const h = 28;
  const max = Math.max(...recent.map((a) => a.ms), 1);
  const stepX = recent.length > 1 ? w / (recent.length - 1) : 0;
  const pts = recent.map((a, i) => {
    const x = (i * stepX).toFixed(2);
    const y = (h - (a.ms / max) * (h - 2) - 1).toFixed(2);
    return `${x},${y}`;
  }).join(' ');
  const chart = svg('svg', {
    viewBox: `0 0 ${w} ${h}`,
    preserveAspectRatio: 'none',
  });
  const baseline = svg('line', {
    x1: '0', x2: String(w), y1: String(h - 1), y2: String(h - 1),
    stroke: 'rgba(0,0,0,0.12)', 'stroke-width': '0.5',
  });
  const poly = svg('polyline', {
    points: pts,
    fill: 'none', stroke: '#2e8a4f', 'stroke-width': '0.9',
    'stroke-linejoin': 'round', 'stroke-linecap': 'round',
    'vector-effect': 'non-scaling-stroke',
  });
  chart.appendChild(baseline);
  chart.appendChild(poly);
  if (recent.length > 0) {
    const last = recent[recent.length - 1]!;
    const lx = ((recent.length - 1) * stepX).toFixed(2);
    const ly = (h - (last.ms / max) * (h - 2) - 1).toFixed(2);
    chart.appendChild(svg('circle', {
      cx: lx, cy: ly, r: '1.4', fill: '#000',
    }));
  }
  card.appendChild(chart);
  return card;
}

type WakeLockSentinelLike = { released: boolean; release: () => Promise<void> };
type WakeLockNav = Navigator & { wakeLock?: { request: (type: 'screen') => Promise<WakeLockSentinelLike> } };

export const ghostImages: RescueModule = {
  id: 'ghost-images',
  name: 'Ghost Images',
  kind: 'reflection',
  defaultDurationSec: 60,
  reducedMotion: true,
  safety: {
    gamblingLike: false,
    excludeForDomains: [],
    experimental: false,
    reducedMotionSafe: true,
  },
  async run(host, _ctx) {
    return new Promise<RescueResult>((resolve) => {
      clear(host);
      document.body.classList.add('ghost-active');

      const stage = el('div', { class: 'rescue-stage' });
      const wrap = el('div', { class: 'ghost-wrap' });
      stage.appendChild(wrap);
      host.appendChild(stage);

      let wakeLock: WakeLockSentinelLike | null = null;
      const requestWakeLock = async (): Promise<void> => {
        const wl = (navigator as WakeLockNav).wakeLock;
        if (!wl) return;
        try { wakeLock = await wl.request('screen'); } catch { wakeLock = null; }
      };
      const releaseWakeLock = (): void => {
        const wl = wakeLock;
        wakeLock = null;
        if (wl && !wl.released) wl.release().catch(() => {});
      };
      const onVisibilityChange = (): void => {
        if (document.visibilityState === 'visible' && !finished) void requestWakeLock();
      };
      document.addEventListener('visibilitychange', onVisibilityChange);

      let history = readHistory();
      let rounds = 0;
      let totalHoldMs = 0;
      let bestThisSessionMs = 0;
      let lastShapeId: string | undefined;
      let finished = false;
      const activeTimers = new Set<number>();

      const setTimer = (cb: () => void, ms: number): number => {
        const id = window.setTimeout(() => {
          activeTimers.delete(id);
          cb();
        }, ms);
        activeTimers.add(id);
        return id;
      };
      const clearTimers = (): void => {
        for (const id of activeTimers) window.clearTimeout(id);
        activeTimers.clear();
      };

      const tryFullscreen = (): void => {
        const root = document.documentElement;
        const req = root.requestFullscreen?.bind(root);
        if (req) req().catch(() => {});
      };
      const exitFullscreen = (): void => {
        if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
      };

      const finish = (): void => {
        if (finished) return;
        finished = true;
        clearTimers();
        document.removeEventListener('visibilitychange', onVisibilityChange);
        releaseWakeLock();
        exitFullscreen();
        document.body.classList.remove('ghost-active');
        const kind: RescueResult['kind'] = rounds > 0 ? 'completed' : 'abandoned';
        resolve({
          kind,
          details: {
            rounds,
            totalHoldMs,
            bestHoldMs: bestThisSessionMs,
            avgHoldMs: rounds > 0 ? Math.round(totalHoldMs / rounds) : 0,
          },
        });
      };

      // ---- screens ----

      const renderMenu = (): void => {
        clear(wrap);
        const menu = el('div', { class: 'ghost-menu' });
        menu.appendChild(el('h2', {}, 'Ghost Images'));
        menu.appendChild(el('p', { class: 'muted' },
          'Train your mind’s eye. Stare, then watch the afterimage fade.'));
        menu.appendChild(buildGraph(history));
        menu.appendChild(el('button', {
          class: 'btn primary block',
          onclick: () => { tryFullscreen(); void requestWakeLock(); startRound(); },
        }, rounds > 0 ? 'Start next round' : 'Start'));
        menu.appendChild(el('button', {
          class: 'btn block',
          onclick: () => renderInstructions(),
        }, 'How it works'));
        menu.appendChild(el('button', {
          class: 'btn block',
          onclick: () => renderAbout(),
        }, 'About this practice'));
        menu.appendChild(el('button', {
          class: 'btn ghost block',
          onclick: () => finish(),
        }, 'Exit'));
        wrap.appendChild(menu);
      };

      const renderAbout = (): void => {
        clear(wrap);
        const box = el('div', { class: 'ghost-instructions' });
        box.appendChild(el('h2', {}, 'About this practice'));

        box.appendChild(el('p', { class: 'ghost-lede' },
          'A short, focused exercise that uses afterimages to train how vividly you can hold a picture in your mind.'));

        const para = (text: string): HTMLElement =>
          el('p', { class: 'ghost-step-text' }, text);

        box.appendChild(el('div', { class: 'ghost-step-title' }, 'Where it comes from'));
        box.appendChild(para(
          'A version of this practice was used by the Pythagoreans over 2,000 years ago — they called it micro meditation. Modern teachers still use it because the underlying trick is simple: a strong, high-contrast shape leaves an afterimage on the retina, and learning to hold that afterimage trains visual attention.'));

        box.appendChild(el('div', { class: 'ghost-step-title' }, 'What it does'));
        box.appendChild(para(
          'With practice, the same circuits that hold the afterimage get better at holding any mental image. People report sharper visualization, stronger memory, more inventive thinking, and easier lucid dreaming. It’s also a quick way to settle the mind — a few rounds work like a short meditation.'));

        box.appendChild(el('div', { class: 'ghost-step-title' }, 'How to use it'));
        box.appendChild(para(
          'Sit comfortably with a still body. Do a few rounds — 5 to 20 minutes is plenty. Once or twice a day, most days, gives the best results. Don’t force it; if the ghost fades, just start the next round.'));

        const note = el('div', { class: 'ghost-tip' });
        note.appendChild(el('span', { class: 'ghost-tip-label' }, 'Heads up'));
        note.appendChild(el('span', { class: 'ghost-tip-text' },
          'The afterimage is inverted — black parts of the shape look bright in your mind, white parts look dark. That’s normal.'));
        box.appendChild(note);

        box.appendChild(el('button', {
          class: 'btn primary block',
          onclick: () => renderMenu(),
        }, 'Back'));
        wrap.appendChild(box);
      };

      const renderInstructions = (): void => {
        clear(wrap);
        const box = el('div', { class: 'ghost-instructions' });
        box.appendChild(el('h2', {}, 'How it works'));
        box.appendChild(el('p', { class: 'ghost-lede' },
          'You’ll stare at a shape, then watch its ghost in your mind. The longer you hold it, the stronger your visualization gets.'));

        const tip = el('div', { class: 'ghost-tip' });
        tip.appendChild(el('span', { class: 'ghost-tip-label' }, 'Before you start'));
        tip.appendChild(el('span', { class: 'ghost-tip-text' },
          'Turn your brightness all the way up. Sit somewhere quiet. Get still and stay still — a calm body holds the ghost longer.'));
        box.appendChild(tip);

        const steps: ReadonlyArray<{ title: string; body: string }> = [
          { title: 'Stare',  body: 'Lock your eyes on the red dot for 30 seconds. Don’t glance around — keep your gaze pinned to the dot, even if you blink.' },
          { title: 'Close',  body: 'When the screen flashes empty, close your eyes right away.' },
          { title: 'Hold',   body: 'A glowing ghost of the shape will appear behind your eyelids. Watch it without moving your eyes — let it sit there as long as it will.' },
          { title: 'Record', body: 'When it fades all the way out, open your eyes and tap “Lost it.” Then try the next round.' },
        ];
        const stepList = el('ol', { class: 'ghost-steps' });
        steps.forEach((s, i) => {
          const li = el('li', { class: 'ghost-step' });
          li.appendChild(el('span', { class: 'ghost-step-num' }, String(i + 1)));
          const body = el('span', { class: 'ghost-step-body' });
          body.appendChild(el('span', { class: 'ghost-step-title' }, s.title));
          body.appendChild(el('span', { class: 'ghost-step-text' }, s.body));
          li.appendChild(body);
          stepList.appendChild(li);
        });
        box.appendChild(stepList);

        const hint = el('div', { class: 'ghost-tip ghost-hint' });
        hint.appendChild(el('span', { class: 'ghost-tip-label' }, 'The trick' ));
        hint.appendChild(el('span', { class: 'ghost-tip-text' },
          'Don’t chase the ghost. The instant your eyes try to scan its edges or pick out details, it slips. Soften your focus, take in the whole shape at once, and let it float. The less you grab at it, the longer it stays.'));
        box.appendChild(hint);

        box.appendChild(el('p', { class: 'ghost-foot' },
          'Each round picks a new shape. Your hold times are saved so you can watch your visualization sharpen over time.'));
        box.appendChild(el('button', {
          class: 'btn primary block',
          onclick: () => renderMenu(),
        }, 'Got it'));
        wrap.appendChild(box);
      };

      const startRound = (): void => {
        const shape = pickShape(lastShapeId);
        lastShapeId = shape.id;
        clear(wrap);
        const stageEl = el('div', { class: 'ghost-stage' });
        const countdownEl = el('div', { class: 'ghost-countdown' }, `${STARE_SEC}`);
        const shapeEl = buildShapeSvg(shape);
        const dotEl = el('div', { class: 'ghost-dot' });
        const exitEl = el('button', {
          class: 'ghost-exit-btn',
          attrs: { type: 'button' },
          onclick: () => finish(),
        }, 'Exit');
        stageEl.appendChild(countdownEl);
        stageEl.appendChild(shapeEl);
        stageEl.appendChild(dotEl);
        stageEl.appendChild(exitEl);
        wrap.appendChild(stageEl);

        // Fade shape in next frame so the transition runs.
        requestAnimationFrame(() => shapeEl.classList.add('visible'));

        const startedAt = performance.now();
        const tick = (): void => {
          if (finished) return;
          const elapsed = (performance.now() - startedAt) / 1000;
          const remain = Math.max(0, STARE_SEC - elapsed);
          countdownEl.textContent = `${Math.ceil(remain)}`;
          if (remain <= 5) stageEl.classList.add('dim');
          if (remain > 0) {
            setTimer(tick, 250);
          } else {
            beginAfterimage();
          }
        };
        tick();
      };

      const beginAfterimage = (): void => {
        if (finished) return;
        clear(wrap);
        const after = el('div', { class: 'ghost-after' });
        const timer = el('div', { class: 'ghost-after-timer' }, '0.0s');
        const lostBtn = el('button', {
          class: 'ghost-lost-btn',
          attrs: { type: 'button' },
        }, 'Lost it');
        const exitBtn = el('button', {
          class: 'ghost-exit-btn',
          attrs: { type: 'button' },
          onclick: () => finish(),
        }, 'Exit');
        after.appendChild(timer);
        after.appendChild(lostBtn);
        after.appendChild(exitBtn);
        wrap.appendChild(after);

        const startedAt = performance.now();
        let rafId = 0;
        const updateTimer = (): void => {
          if (finished) return;
          const ms = performance.now() - startedAt;
          timer.textContent = `${(ms / 1000).toFixed(1)}s`;
          rafId = requestAnimationFrame(updateTimer);
        };
        rafId = requestAnimationFrame(updateTimer);

        lostBtn.addEventListener('click', () => {
          cancelAnimationFrame(rafId);
          const ms = performance.now() - startedAt;
          recordRound(ms);
          renderRoundResult(ms);
        }, { once: true });
      };

      const recordRound = (ms: number): void => {
        rounds += 1;
        totalHoldMs += ms;
        if (ms > bestThisSessionMs) bestThisSessionMs = ms;
        history = [...history, { at: new Date().toISOString(), ms }];
        writeHistory(history);
      };

      const renderRoundResult = (ms: number): void => {
        clear(wrap);
        const box = el('div', { class: 'ghost-result' });
        const previousBest = history.slice(0, -1).reduce((m, a) => Math.max(m, a.ms), 0);
        const isPb = ms > previousBest && history.length > 1;
        box.appendChild(el('div', { class: 'ghost-graph-head' },
          el('span', {}, 'You held it for')));
        box.appendChild(el('div', { class: 'ghost-result-time' }, formatMs(ms)));
        if (isPb) {
          box.appendChild(el('div', { class: 'ghost-result-delta up' }, 'New personal best'));
        } else if (previousBest > 0) {
          box.appendChild(el('div', { class: 'ghost-result-delta' },
            `Best: ${formatMs(previousBest)}`));
        }
        box.appendChild(buildGraph(history));
        box.appendChild(el('button', {
          class: 'btn primary block',
          onclick: () => startRound(),
        }, 'Next shape'));
        box.appendChild(el('button', {
          class: 'btn block',
          onclick: () => renderMenu(),
        }, 'Back to menu'));
        box.appendChild(el('button', {
          class: 'btn ghost block',
          onclick: () => finish(),
        }, 'End session'));
        wrap.appendChild(box);
      };

      renderMenu();
    });
  },
};
